function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useRef, useState, useMemo } from 'react';
import { useFloating, flip, shift, offset, hide, arrow, useHover, useInteractions, safePolygon, useFocus, useClick, useDismiss, autoUpdate, useRole } from '@floating-ui/react';
import { omitKeys, useControlled, useForwardedRef } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';
import Tooltip from './Tooltip';
function useTooltip(_ref) {
  var _middlewareData$hide2, _middlewareData$arrow, _middlewareData$arrow2;
  let {
    enableCloseOnClickOutside,
    placement: placementProp,
    show: showProp,
    trigger,
    triggerTarget,
    delayShow,
    delayHide,
    onHide,
    onShow,
    targetRef,
    overlayRef,
    offset: offsetProp,
    variant,
    hideArgs = {
      strategy: 'referenceHidden'
    }
  } = _ref;
  const isClickControlled = trigger === Tooltip.TRIGGER_EVENTS.CLICK;
  const interactive = triggerTarget === Tooltip.TRIGGER_TARGETS.TOOLTIP;
  const mutableTargetRef = useForwardedRef(targetRef || null);
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useControlled(false, showProp);
  const [referenceElement, setReferenceElement] = useState(mutableTargetRef === null || mutableTargetRef === void 0 ? void 0 : mutableTargetRef.current);
  const [floatingElement, setFloatingElement] = useState(overlayRef === null || overlayRef === void 0 ? void 0 : overlayRef.current);
  const {
    context,
    placement,
    middlewareData,
    floatingStyles
  } = useFloating({
    elements: {
      reference: referenceElement,
      floating: floatingElement
    },
    placement: placementProp,
    open: isOpen,
    onOpenChange: open => {
      if (open) {
        onShow === null || onShow === void 0 ? void 0 : onShow();
      } else {
        onHide === null || onHide === void 0 ? void 0 : onHide();
      }
      setIsOpen(open);
    },
    whileElementsMounted: autoUpdate,
    middleware: [offset(offsetProp), flip(), shift(), arrow({
      element: arrowRef
    }), hide(hideArgs)]
  });
  const focus = useFocus(context, {
    enabled: !isClickControlled
  });
  const hover = useHover(context, {
    enabled: !isClickControlled,
    delay: {
      open: delayShow,
      close: delayHide
    },
    handleClose: interactive ? safePolygon({}) : undefined
  });
  const click = useClick(context, {
    enabled: isClickControlled
  });
  const dismiss = useDismiss(context, {
    outsidePress: enableCloseOnClickOutside
  });
  const role = useRole(context, {
    role: 'tooltip'
  });
  const interactions = useInteractions([focus, hover, click, dismiss, role]);
  const referenceRef = useCallback(node => {
    if (mutableTargetRef) {
      mutableTargetRef.current = node;
    }
    setReferenceElement(node);
  }, [mutableTargetRef]);
  const floatingRef = useCallback(node => {
    if (overlayRef) {
      overlayRef.current = node;
    }
    setFloatingElement(node);
  }, [overlayRef]);
  const getReferenceProps = useCallback(userProps => _objectSpread({}, interactions.getReferenceProps(_objectSpread(_objectSpread({
    tabIndex: 0
  }, userProps), {}, {
    ref: referenceRef
  }))), [interactions, referenceRef]);
  const allFloatingStyle = useMemo(() => {
    var _middlewareData$hide;
    return _objectSpread(_objectSpread({}, floatingStyles), {}, {
      visibility: (_middlewareData$hide = middlewareData.hide) !== null && _middlewareData$hide !== void 0 && _middlewareData$hide.referenceHidden ? 'hidden' : 'visible'
    });
  }, [floatingStyles, (_middlewareData$hide2 = middlewareData.hide) === null || _middlewareData$hide2 === void 0 ? void 0 : _middlewareData$hide2.referenceHidden]);
  const getFloatingProps = useCallback(userProps => interactions.getFloatingProps(_objectSpread(_objectSpread({}, userProps), {}, {
    ref: floatingRef,
    style: Object.assign(allFloatingStyle, userProps === null || userProps === void 0 ? void 0 : userProps.style),
    onClick: event => {
      var _userProps$onClick;
      event.stopPropagation();
      userProps === null || userProps === void 0 ? void 0 : (_userProps$onClick = userProps.onClick) === null || _userProps$onClick === void 0 ? void 0 : _userProps$onClick.call(userProps, event);
    }
  })), [floatingRef, allFloatingStyle, interactions]);
  const arrowTransform = `translate(${Math.round(((_middlewareData$arrow = middlewareData.arrow) === null || _middlewareData$arrow === void 0 ? void 0 : _middlewareData$arrow.x) || 0)}px,${Math.round(((_middlewareData$arrow2 = middlewareData.arrow) === null || _middlewareData$arrow2 === void 0 ? void 0 : _middlewareData$arrow2.y) || 0)}px)`;
  const arrowThemeVariants = {
    dark: theme.tokens.colors.background.inverse.subtle.value,
    medium: theme.tokens.colors.background.inverse.selected.value,
    light: theme.tokens.colors.text.inverse.value
  };
  const arrowTheme = arrowThemeVariants[variant || 'dark'];
  const getArrowProps = useCallback(userProps => _objectSpread({
    ref: arrowRef,
    placement,
    size: `5px`,
    color: arrowTheme,
    style: [{
      transform: arrowTransform
    }, userProps === null || userProps === void 0 ? void 0 : userProps.style]
  }, omitKeys(userProps, ['style'])), [arrowTheme, arrowTransform, placement]);
  return {
    show: isOpen,
    placement,
    interactive,
    getReferenceProps,
    getFloatingProps,
    getArrowProps
  };
}
export default useTooltip;
//# sourceMappingURL=useTooltip.js.map